import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Zap, Crown, Building, Building2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/lib/authContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const tiers = [
  {
    key: "free",
    name: "Free",
    price: 0,
    annualPrice: 0,
    icon: Zap,
    tagline: "Explore the platform. See what's possible.",
    features: [
      "1 roadmap",
      "Basic permit list",
      "Basic timeline",
      "5 AI questions",
    ],
    excluded: ["No PDF export", "No location map", "No financial forecast", "No post-opening mode"],
    cta: "Start free",
    ctaSub: "No credit card required",
    popular: false,
    buttonVariant: "outline" as const,
  },
  {
    key: "pro",
    name: "Pro",
    price: 99,
    // $990/year billed annually ≈ $82.50/mo (2 months free)
    annualPrice: 82,
    annualTotal: 990,
    icon: Crown,
    tagline: "Everything you need to open and operate.",
    features: [
      "10 roadmaps",
      "PDF export & financial forecast",
      "Location map",
      "Unlimited AI assistant",
      "Post-opening operations mode",
      "Health inspection prep",
      "Lease tracker",
      "Permit renewal reminders",
      "Restaurant survival score",
      "Priority support",
    ],
    excluded: [],
    cta: "Start growing",
    ctaSub: null,
    popular: true,
    buttonVariant: "default" as const,
  },
  {
    key: "studio",
    name: "Studio",
    price: 179,
    // $1,790/year billed annually ≈ $149.17/mo (2 months free)
    annualPrice: 149,
    annualTotal: 1790,
    icon: Building,
    tagline: "For teams scaling multiple concepts.",
    features: [
      "25 roadmaps",
      "Everything in Pro",
      "5 team members",
      "White-label PDF",
      "Analytics dashboard",
      "Dedicated account manager",
    ],
    excluded: [],
    cta: "Scale up",
    ctaSub: null,
    popular: false,
    buttonVariant: "outline" as const,
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: null,
    annualPrice: null,
    icon: Building2,
    tagline: "Custom solutions for franchises and organizations.",
    features: [
      "Unlimited everything",
      "SSO",
      "API access",
      "Custom integrations",
      "Dedicated onboarding",
    ],
    excluded: [],
    cta: "Contact us",
    ctaSub: null,
    popular: false,
    buttonVariant: "outline" as const,
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  const handleSelect = async (key: string) => {
    if (key === "enterprise") { navigate("/enterprise"); return; }
    if (key === "free") { if (!user) navigate("/auth"); else navigate("/dashboard"); return; }
    if (!user) { navigate("/auth"); return; }
    // Map the displayed plan key to the backend plan_type: "studio" → "business".
    const backendPlan = key === "studio" ? "business" : key;
    if (!["pro", "business"].includes(backendPlan)) {
      navigate(`/checkout?plan=${key}&billing=${annual ? "yearly" : "monthly"}`);
      return;
    }
    try {
      setLoadingKey(key);
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { plan: backendPlan, billing: annual ? "yearly" : "monthly", origin: window.location.origin },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url as string;
        return;
      }
      throw new Error("No checkout URL returned");
    } catch (err) {
      console.error(err);
      toast.error("Could not start checkout. Please try again.");
      setLoadingKey(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="container flex items-center justify-between py-5">
        <span className="font-heading text-xl font-medium tracking-tight text-foreground cursor-pointer" onClick={() => navigate("/")}>
          oplif<span className="text-primary">.</span>
        </span>
        <div className="flex items-center gap-3">
          {user ? (
            <Button size="sm" variant="outline" onClick={() => navigate("/dashboard")}>Dashboard</Button>
          ) : (
            <Button size="sm" onClick={() => navigate("/auth")}>Sign in</Button>
          )}
        </div>
      </nav>

      <section className="container pt-8 pb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl font-medium text-foreground">
            Plans that grow with your business
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-xl mx-auto">
            Start free. Upgrade as you move from planning to operating.
          </p>
          <div className="flex items-center justify-center gap-3 mt-8">
            <span className={`text-sm font-medium ${!annual ? "text-foreground" : "text-muted-foreground"}`}>Monthly</span>
            <Switch checked={annual} onCheckedChange={setAnnual} />
            <span className={`text-sm font-medium ${annual ? "text-foreground" : "text-muted-foreground"}`}>
              Annual
            </span>
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-500 text-white">Save 2 months</span>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16 items-start max-w-6xl mx-auto">
          {tiers.map((t, i) => {
            const Icon = t.icon;
            const showAnnual = annual && t.price !== null && t.price > 0;
            const displayPrice = showAnnual ? t.annualPrice : t.price;
            const annualTotal = "annualTotal" in t ? t.annualTotal : null;

            return (
              <motion.div
                key={t.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.07 }}
                className={`relative bg-card rounded-2xl border flex flex-col transition-all duration-300 ${
                  t.popular
                    ? "border-emerald-400/40 shadow-xl ring-2 ring-emerald-400/20 p-6 lg:scale-[1.03] lg:-my-2 z-10"
                    : "border-border hover:border-primary/15 hover:shadow-md p-5"
                }`}
              >
                {t.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[11px] font-medium px-3 py-0.5 rounded-full tracking-wide whitespace-nowrap">
                    Most Popular
                  </div>
                )}

                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${
                  t.popular ? "bg-emerald-500/10" : "bg-primary/8"
                }`}>
                  <Icon className={`w-[18px] h-[18px] ${t.popular ? "text-emerald-500" : "text-primary"}`} />
                </div>

                <h3 className="font-heading font-medium text-foreground text-base">{t.name}</h3>
                <p className="text-xs text-muted-foreground/70 mt-1 leading-snug mb-4">{t.tagline}</p>

                <div className="mb-5">
                  {displayPrice === null ? (
                    <span className="font-heading text-3xl font-medium text-foreground">Custom</span>
                  ) : (
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-heading text-3xl font-medium text-foreground">${displayPrice}</span>
                      <span className="text-sm text-muted-foreground">/mo</span>
                      {showAnnual && (
                        <span className="text-sm text-muted-foreground/50 line-through">${t.price}/mo</span>
                      )}
                    </div>
                  )}
                  {showAnnual && annualTotal && (
                    <p className="text-[11px] text-muted-foreground/70 mt-1">
                      ${annualTotal} billed yearly · 2 months free
                    </p>
                  )}
                </div>

                <ul className="space-y-2 mb-2 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className={`w-4 h-4 shrink-0 mt-0.5 ${t.popular ? "text-emerald-500" : "text-primary"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                  {t.excluded.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground/40 line-through">
                      <span className="w-4 h-4 shrink-0 mt-0.5 text-center">—</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-4">
                  <Button
                    variant={t.buttonVariant}
                    className={`w-full gap-2 ${
                      t.popular
                        ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-md"
                        : ""
                    }`}
                    onClick={() => handleSelect(t.key)}
                    disabled={loadingKey === t.key}
                  >
                    {loadingKey === t.key ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Redirecting…
                      </>
                    ) : (
                      <>
                        {t.cta}
                        <ArrowRight className="w-3.5 h-3.5" />
                      </>
                    )}
                  </Button>
                  {t.ctaSub && (
                    <p className="text-[11px] text-muted-foreground/60 text-center mt-1.5">{t.ctaSub}</p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
